import React, { useMemo } from 'react';
import TextsHeader from '../TextsHeader';

import { Legend, Min, Recomended } from '../graphs/styles';
import ChartBarTotal from '../graphs/ChartBarTotal';
import { Col } from 'antd';

import {
  Container,
  Content,
  ColContent,
  Middle,
  LineDashed,
  ColM,
  Line,
  LineDashedTop
} from './styles'
import TextSuggestion from './TextSuggestion';
import {
  CFP_MODULES,
  getRecommendedByExamType,
  MODULE_COUNT,
  getRomanModule
} from '../constants/examRecommendations';
// import { isEmpty } from 'lodash';

const normalizeModuleCode = (value) => {
  if (value === undefined || value === null) return '';

  const text = String(value).trim().toUpperCase();
  if (!text) return '';
  if (text.startsWith('M')) return text;

  const onlyNumbers = text.replace(/\D/g, '');
  return onlyNumbers ? `M${onlyNumbers}` : '';
};

const normalizeChartPercent = (percent) => {
  const numericPercent = Number(percent);

  if (!Number.isFinite(numericPercent) || numericPercent <= 0) return 0;
  if (numericPercent <= 10) return 100;
  if (numericPercent > 100) return 100;
  return Math.round(numericPercent);
};

const formatPercent = (value) => `${Number(value).toFixed(1).replace('.', ',')}%`;

const getStudentPercentByModule = (moduleCode, modulesTotalByCode, modulePerformanceByCode) => {
  const simulatedData = modulesTotalByCode[moduleCode];
  if (Number.isFinite(Number(simulatedData?.nota_percentual))) {
    return Number(simulatedData.nota_percentual);
  }

  if (Number.isFinite(Number(simulatedData?.total))) {
    return Number((Number(simulatedData.total) * 100).toFixed(1));
  }

  const modulePerf = modulePerformanceByCode[moduleCode];
  if (Number.isFinite(Number(modulePerf?.total))) {
    return Number((Number(modulePerf.total) * 100).toFixed(1));
  }

  return 0;
};

function GeralResults(props) {
  const examType = props.examType || 'cfp';
  const moduleCount = MODULE_COUNT[examType];
  const isCFA = examType === 'cfa';

  const RECOMMENDED_ART = getRecommendedByExamType(examType);

  // Processar dados baseado no tipo de exame
  let modulesTotal = Array(moduleCount).fill(0);
  const completeNumbers = Array.from({ length: moduleCount }, (_, i) => i);

  if (examType === 'cfa') {
    // CFA: dados vêm como array simples com total (decimal)
    const cfaData = props.data?.module_performance ?? [];
    
    // Processar até 10 módulos
    for (let index = 0; index < moduleCount; index++) {
      const item = cfaData[index];
      if (item && item.total !== undefined) {
        modulesTotal[index] = {
          modules: `M${index + 1}`,
          nota_percentual: Math.round(item.total * 100), // Converter decimal para porcentagem
          total: item.total
        };
      } else {
        // Garantir estrutura mesmo sem dados
        modulesTotal[index] = {
          modules: `M${index + 1}`,
          nota_percentual: 0,
          total: 0
        };
      }
    }
  } else {
    // CFP: formato original
    const data = props.data?.module_performance_simulate ?? [];
    
    completeNumbers.map((item, x) => {
      if (Object.values(data)[x] !== undefined && Object.values(data)[x].modules) {
        modulesTotal[parseInt(Object.values(data)[x].modules.substr(1)) - 1] = Object.values(data)[x];
      }
      return 0;
    });
  }

  const toImprove = props?.data?.you_can_improve_simulated ?? [];

  toImprove.sort((a, b) => {
    return a.module - b.module;
  });

  const moduleToImprove = modulesTotal?.filter((item) => {
    const index = RECOMMENDED_ART.findIndex((element) => element.module === item?.modules);
    return RECOMMENDED_ART[index]?.value - item?.nota_percentual > 0
  });
  // Extrair número do módulo corretamente (M1 -> 1, M10 -> 10)
  const moduleToImproveArray = moduleToImprove.map(item => item.modules.substring(1));
  let modulesToImprove = '';
  if (moduleToImproveArray.length > 1) {
    modulesToImprove = moduleToImproveArray.slice(0, -1).join(', ') + ' e ' + moduleToImproveArray.slice(-1);
  } else {
    modulesToImprove = moduleToImproveArray.join(', ');
  }
  const calculateImproveModule = (item) => {
    const index = RECOMMENDED_ART.findIndex((element) => element.module === item.modules);
    return  Math.round(RECOMMENDED_ART[index]?.value - item?.nota_percentual);
  }

  const modulePerformanceByCode = useMemo(() => {
    return (props?.data?.module_performance ?? []).reduce((acc, item) => {
      const moduleCode = normalizeModuleCode(item?.module);
      if (!moduleCode) return acc;

      acc[moduleCode] = item;
      return acc;
    }, {});
    //eslint-disable-next-line
  }, [props?.data?.module_performance]);

  const modulesTotalByCode = useMemo(() => {
    return (modulesTotal ?? []).reduce((acc, item) => {
      const moduleCode = normalizeModuleCode(item?.modules);
      if (!moduleCode) return acc;

      acc[moduleCode] = item;
      return acc;
    }, {});
  }, [modulesTotal]);

  const cfpOfficialSummary = useMemo(() => {
    if (examType !== 'cfp') {
      return [];
    }

    return CFP_MODULES.map((moduleItem) => {
      const moduleCode = `M${moduleItem.module}`;

      return {
        moduleCode,
        moduleLabel: `Módulo ${getRomanModule(moduleItem.module)} – ${moduleItem.name}`,
        officialPercent: formatPercent(moduleItem.weight),
        studentPercent: formatPercent(
          getStudentPercentByModule(moduleCode, modulesTotalByCode, modulePerformanceByCode)
        )
      };
    });
  }, [examType, modulesTotalByCode, modulePerformanceByCode]);

  return (
    <Container style={{marginTop: "80px"}}>
      <TextsHeader>
        <p>Agora veja abaixo o seu desempenho de forma geral em todos os simulados, comparando com nossa recomendação e também comparando com o mínimo de aprovação:</p>
      </TextsHeader>

      <Content isCFA={isCFA}>
        <ColContent xs={24} className="reportSeven" isCFA={isCFA}>
          {modulesTotal?.map((item, i) => {
            const recomended = RECOMMENDED_ART[i]?.value || 0;

            const result = normalizeChartPercent(item?.nota_percentual);

            return (
              <div key={item?.modules ?? `M${i + 1}`} style={{
                marginLeft: '0',
                display: 'flex',
                justifyContent: 'center',
                flex: isCFA ? undefined : 1
              }}>
                <ChartBarTotal 
                  result={result} 
                  margin={1} 
                  recomended={recomended} 
                  isCFA={isCFA} 
                />
              </div>
            )
          })}
          <Middle isCFA={isCFA}>
            <LineDashedTop isCFA={isCFA} />
            <p style={{ fontSize: isCFA ? '1vw' : '1.2vw' }}>50%</p>
            <LineDashed />
          </Middle>
        </ColContent>

        <Line isCFA={isCFA} />

        <ColM isCFA={isCFA}>
          {
            completeNumbers.map((item => {
              return <h3 key={item}>M{item+1}</h3>
            }))
          }
        </ColM>
        <Col xs={24}>
          <Legend style={{ 
            marginTop: '1vw', 
            justifyContent: 'flex-end' 
          }}>
            <Min>
              <div />
              <p>seu desempenho</p>
            </Min>
            <Recomended>
              <div />
              <p>recomendado</p>
            </Recomended>
          </Legend>
        </Col>
      </Content>

      {examType === 'cfp' && (
        <div style={{ marginTop: '3vw' }}>
          <div style={{ width: '100%', marginBottom: '2vw' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '3fr 1fr 1fr',
              gap: '1vw',
              paddingBottom: '0.8vw',
              borderBottom: '1px solid #D6D6D6',
              fontFamily: 'Work Sans, sans-serif',
              fontSize: '1.15vw',
              fontWeight: 700,
              color: '#000533'
            }}>
              <span>Módulo oficial</span>
              <span>% oficial</span>
              <span>% acertos</span>
            </div>

            {cfpOfficialSummary.map((moduleItem) => (
              <div
                key={moduleItem.moduleCode}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '3fr 1fr 1fr',
                  gap: '1vw',
                  padding: '0.7vw 0',
                  borderBottom: '1px solid #EFEFEF',
                  fontFamily: 'Work Sans, sans-serif',
                  fontSize: '1.05vw',
                  color: '#434343'
                }}
              >
                <span>{moduleItem.moduleLabel}</span>
                <span>{moduleItem.officialPercent}</span>
                <span>{moduleItem.studentPercent}</span>
              </div>
            ))}
          </div>

          <TextSuggestion>
              {moduleToImprove?.length > 0 ? (
                <>
                  <TextSuggestion>
                    Você precisa dar mais ênfase aos módulos<span> {modulesToImprove}</span>. Para ser mais exato você precisa: <br />
                  </TextSuggestion>
                </>
              ) : null}

              {moduleToImprove?.map((item) =>
                <React.Fragment key={`module-to-improve-${item?.modules}`}>
                  Módulo <span>{item?.modules.substring(1)}</span>: aumentar <span>{calculateImproveModule(item)}%</span>.<br />
                </React.Fragment>
              )}
          </TextSuggestion>
        </div>
      )}
    </Container>
  );
};

export default GeralResults;
